import React, { useState, useEffect, useRef } from 'react';
import axiosClient from '../utils/apiClient';
import { useParams, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { API_BASE_URL } from '../utils/urls.js'; 

const InvoiceDetail = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [clientName, setClientName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('user'));
  const fallbackOrgName = user?.organization?.name || 'N/A';
  const fallbackOrgAddress = user?.organization?.orgAddress || '';
  const fallbackLogoUrl = user?.organization?.logoUrl || '';

  const invoiceRef = useRef();

  const getLogoUrl = (path) => {
      if (!path) return null;
      if (path.startsWith('http')) return path;
      const baseUrl = API_BASE_URL.split('/api')[0];
      return `${baseUrl}${path}`;
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await axiosClient.get(`/invoices/${id}`);
        const data = res.data;
        setInvoice(data);

        let name = "Unknown Client";
        if (data.is_agency === 1) {
          name = data.agency?.trim() || "Unnamed Agency";
        } else {
          const first = data.client_first?.trim() || "";
          const last = data.client_last?.trim() || "";
          name = `${first} ${last}`.trim() || "Unnamed Client";
        }
        setClientName(name);

      } catch (err) {
        setError(err.response?.data?.message || "Failed to load invoice details.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id]);

  const handleDownloadPdf = async () => {
    const element = invoiceRef.current;
    
    const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff' 
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight);
    pdf.save(`Invoice_${invoice.invoice_number || 'details'}.pdf`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'unpaid': return 'bg-red-100 text-red-800 border-red-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading invoice...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
  if (!invoice) return <div className="p-8 text-center text-gray-500">Invoice not found.</div>;

  const displayOrgName = invoice.organization_name || fallbackOrgName;
  const displayOrgAddress = invoice.org_address || fallbackOrgAddress;
  const displayLogoUrl = getLogoUrl(invoice.logo_url || fallbackLogoUrl);

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Invoice Details</h1>
          <p className="text-sm text-gray-500">View and manage this invoice</p>
        </div>
        <button
          onClick={handleDownloadPdf}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg shadow-sm font-medium transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
          Download PDF
        </button>
      </div>

      <div 
        ref={invoiceRef} 
        className="bg-white p-10 rounded-xl shadow-lg border border-gray-200 text-gray-800"
      >
        <div className="flex justify-between items-start mb-10 border-b pb-8">
          <div className="max-w-sm">
            {displayLogoUrl ? (
                <img 
                    src={displayLogoUrl} 
                    alt={`${displayOrgName} Logo`} 
                    className="h-16 sm:h-20 object-contain mb-3"
                    crossOrigin="anonymous" 
                />
            ) : (
                <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide mb-1">
                  {displayOrgName}
                </h2>
            )}

            {displayOrgAddress && (
                <p className="text-gray-600 whitespace-pre-wrap text-sm leading-relaxed mb-4">
                    {displayOrgAddress}
                </p>
            )}

            <div className="text-sm font-bold text-blue-600 tracking-widest mb-4">INVOICE</div>
            <div className="text-gray-500 text-sm">Invoice #: <span className="text-gray-800 font-medium">{invoice.invoice_number}</span></div>
          </div>
          
          <div className="text-right">
            <div className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold border ${getStatusColor(invoice.status)}`}>
              {invoice.status.toUpperCase()}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Date: {new Date(invoice.issue_date).toLocaleDateString()}
            </div>
          </div>
        </div>

        <div className="mb-10">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="w-1/2 pb-3 text-xs font-bold text-gray-400 uppercase tracking-wider border-b">Bill To</th>
                <th className="w-1/2 pb-3 text-xs font-bold text-gray-400 uppercase tracking-wider border-b pl-8">Case Details</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="pt-4 align-top pr-4">
                  <div className="font-bold text-lg text-gray-900 mb-1">{clientName}</div>
                  <div className="text-gray-600 text-sm">{invoice.client_email || 'No email provided'}</div>
                  <div className="text-gray-600 text-sm mt-1">{invoice.client_phone || ''}</div>
                </td>
                <td className="pt-4 align-top pl-8 border-l border-gray-100">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-1 text-gray-500 w-32">Case Name:</td>
                        <td className="py-1 font-medium text-blue-600">
                           {invoice.case_id ? (
                            <Link to={`/cases/${invoice.case_id}`} className="hover:underline">
                              {invoice.case_name || 'View Case'}
                            </Link>
                          ) : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1 text-gray-500">Reference #:</td>
                        <td className="py-1 text-gray-900">{invoice.case_number || 'N/A'}</td>
                      </tr>
                      {invoice.is_pi === 0 && (
                        <tr>
                          <td className="py-1 text-gray-500">Served:</td>
                          <td className="py-1 text-gray-900">{invoice.document_served || '-'}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mb-10">
          <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Description</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <p className="font-medium">Legal Services / Case Work</p>
                  <p className="text-gray-500 text-xs mt-1">{invoice.case_description || 'No additional details provided.'}</p>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">
                  ${parseFloat(invoice.total_amount).toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mb-8">
          <div className="w-1/2 sm:w-1/3">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-500">Subtotal</td>
                  <td className="py-2 text-right font-medium text-gray-900">
                    ${parseFloat(invoice.total_amount).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 text-gray-900 font-bold text-base">Total Due</td>
                  <td className="py-3 text-right font-bold text-base text-blue-600">
                    ${parseFloat(invoice.total_amount).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* NEW ADDITION: INVOICE DETAILS PRINT BLOCK */}
        {invoice.invoice_details && (
          <div className="mt-4 bg-gray-50 p-5 rounded-lg border border-gray-100">
            <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-2 border-b border-gray-200 pb-2">Invoice Details</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {invoice.invoice_details}
            </p>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-gray-100 flex justify-between items-center">
            <div className="text-xs text-gray-400">
                <p>Due Date: {new Date(invoice.due_date).toLocaleDateString()}</p>
                <p>Thank you for your business.</p>
            </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;